import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
} from '@microsoft/signalr';
import { LocalStorageItems } from '@shared/constants/local-storage-item';
import { Subject } from 'rxjs';
import { UserMetadataService } from './user-metadata.service';
import { ReleaseNotesService } from '@features/release-notes-dialog/release-notes.service';

@Injectable({ providedIn: 'root' })
export class ReleaseSyncService {
  private metaDataService = inject(UserMetadataService);
  private releaseNotesService = inject(ReleaseNotesService);

  displayBannerTrigger$ = new Subject<boolean>();
  private hubConnection: HubConnection;

  setupReleaseSyncConnection(): void {
    this.metaDataService.fetchLastDbReleaseVersion().subscribe({
      next: resp => {
        this.checkNewVersion(resp, true);
        this.setupReleaseHubConnection();
      },
      error: () => this.setupReleaseHubConnection(),
    });
  }

  reloadApp(): void {
    window.location.reload();
  }

  private setupReleaseHubConnection(): void {
    const builder = new HubConnectionBuilder();
    this.hubConnection = builder
      .withUrl(environment.endpoints.publicHubApi, {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect()
      .build();

    this.hubConnection.start();

    this.hubConnection.on('NewVersion', newVersion => {
      this.checkNewVersion(newVersion);
    });
  }

  private checkNewVersion(
    latestDbVersion: string,
    isFirstPageLoad = false
  ): void {
    const lastCustomerVersion = this.getLatestRenewedVersion();
    if (lastCustomerVersion !== latestDbVersion) {
      localStorage.setItem(
        LocalStorageItems.LatestRenewedVersion,
        latestDbVersion
      );

      if (isFirstPageLoad) {
        this.setShowReleaseDialog('true');
        this.releaseNotesService.checkForNewRelease();
      } else {
        this.displayBannerTrigger$.next(true);
      }
    }
  }

  private setShowReleaseDialog(value: 'true' | 'false'): void {
    localStorage.setItem(LocalStorageItems.ShowReleaseDialog, value);
  }

  private getLatestRenewedVersion(): string {
    return localStorage.getItem(LocalStorageItems.LatestRenewedVersion) || '';
  }
}
